import React, { useContext, Suspense, lazy } from 'react'
import { Grid, Container, Divider, useMediaQuery } from '@material-ui/core'
import {
  useRouteMatch,
  Switch,
  Route,
  useHistory,
  useLocation,
  Redirect,
} from 'react-router-dom'
import {
  EventEmitterService,
  EventKey,
} from '../../services/EventEmitterService'
import UserProfileContext from '../../context/UserProfileContext'
import { FlowProtectedRoute } from '../Core/Routes/FlowProtectedRoute'
import { SessionContextStore } from '../../context/SessionContext'
import ViolationsService from '../../services/ViolationsService'
import { BrowserView, MobileView, isMobile } from 'react-device-detect'
import { InsurancePurchaseContextStore } from '../../context/InsuracePurchaseContext'
import { InsuranceBillingPeriod } from '../../models/insurance-models/InsuranceBillingPeriodModel'
import { SessionService } from '../../services/SessionService'
import FlexView from 'react-flexview/lib'
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace'
import { lazyWithPreload } from '../../utilities/RLazyLoading'
import { BlueGrayBox } from '../Core/BlueGrayBox'
import { billingPeriodPretty, isInFlow } from '../../services/utils'
import {
  FirebaseService,
  FirebaseConfigKey,
} from '../../services/FirebaseService'
import { COUNTRIES } from '../../Constants'
import { InsurerLogo } from '../Core/InsurerLogo'
import { ElevatedCircle } from '../Core/ElevatedCircle'
import Error from '@material-ui/icons/Error'
import { GuidelinesSummarySection } from './SummarySection/GuidelinesSummarySection'
import { WhatsCoveredSummarySection } from './SummarySection/WhatsCoveredSummarySection'
import DataLayerService from '../../services/DataLayerService'
import { PasswordlessEmailForm } from '../AbTestComponents/PasswordlessEmailForm'
import GoogleOptimizeService, {
  EXPERIMENTS,
  VARIANTS,
} from '../../services/GoogleOptimizeService'
import { LoginProtectedRoute } from '../Core/Routes/LoginProtectedRoute'
import { OwnersFlow } from './OwnersFlow'
import { CustomizeMotorcycle } from './CustomizeMotorcycle'
import { CustomizeOperators } from './CustomizeOperators'
import { CustomizeCoverages } from './Coverages/CustomizeCoverages'
import { CoveragePicker } from './CoveragePicker'
import { PlanSummary } from './Plans/PlanSummary'
import { Issuance } from './Issuance'
import { PilotDetails } from '../Flow/PilotDetails'

const CustomizePolicy = lazyWithPreload(() =>
  import('./CustomizePolicy').then((module) => ({
    default: module.CustomizePolicy,
  }))
)
CustomizePolicy.preload()
const InsuranceSetupSummary = lazyWithPreload(() =>
  import('./InsuranceSetupSummary').then((module) => ({
    default: module.InsuranceSetupSummary,
  }))
)
const UserProfile = lazyWithPreload(() =>
  import('../UserProfile/UserProfile').then((module) => ({
    default: module.UserProfile,
  }))
)
const Congratulations = lazyWithPreload(() =>
  import('./Congratulations').then((module) => ({
    default: module.Congratulations,
  }))
)
const CheckoutForm = lazyWithPreload(() =>
  import('../CreditCard/CheckoutForm').then((module) => ({
    default: module.CheckoutForm,
  }))
)
const SkywatchProgressBar = lazy(() =>
  import('../Core/SkywatchProgressBar').then((module) => ({
    default: module.SkywatchProgressBar,
  }))
)
const PolicySummary = lazyWithPreload(() =>
  import('./PolicySummary').then((module) => ({
    default: module.PolicySummary,
  }))
)
const FlowLogin = lazyWithPreload(() =>
  import('../Login/FlowLogin').then((module) => ({
    default: module.FlowLogin,
  }))
)

export const MonthlyInsurance = () => {
  const sessionContext = useContext(SessionContextStore)
  const insurancePurchaseContext = useContext(InsurancePurchaseContextStore)
  const { path, url } = useRouteMatch()
  const history = useHistory()
  const location = useLocation()
  const isMobile = !useMediaQuery((theme) => theme.breakpoints.up('md'))

  React.useEffect(() => {
    setTimeout(() => {
      UserProfile.preload()
      InsuranceSetupSummary.preload()
      Congratulations.preload()
      CheckoutForm.preload()
      PolicySummary.preload()
      FlowLogin.preload()
    }, 500)
  }, [])

  React.useEffect(() => {
    if (
      sessionContext.activeInsurance &&
      sessionContext.activeInsurance.insuranceId &&
      !SessionService.isBroker() &&
      location.pathname !== '/insurance/congratulations' &&
      !sessionStorage.getItem('hadPolicyPopup')
    ) {
      EventEmitterService.dispatch(EventKey.ShowSuccess, {
        title: 'New Insurance?',
        body: 'You already have an active policy',
        icon: (
          <ElevatedCircle>
            {' '}
            <Error className="blue" />
          </ElevatedCircle>
        ),
        actionText: 'View my active policies',
        onClose: (val) => {
          if (val == 1) history.push('/policy-management')
          else {
            sessionStorage.setItem('hadPolicyPopup', true)
          }
        },
        closeBelowAction: () => {},
        closeBelowText: 'Purchase new policy',
      })
    }
  }, [sessionContext.activeInsurance, location])

  const isInitialMount = React.useRef(true)

  React.useEffect(() => {
    const handleFlow = (eventKey) => {
      switch (eventKey) {
        case EventKey.FLOW_CUSTOMIZE_FINISH:
          history.push(url + '/summary')
          break
        case EventKey.FLOW_PROFILE_FINISH:
          // if (sessionContext.user.userInsuredPersons.length > 0)
          //     handleFlow(EventKey.FLOW_PILOT_FINIISH)
          // else
          if (location.pathname == '/insurance/profile') {
            history.push(url + '/pilot')
          }
          break
        case EventKey.FLOW_PILOT_FINIISH:
          history.push(url + '/customize')
          break
        case EventKey.FLOW_COVERAGE_PLAN_PICKED:
          // history.push('/thankyou')
          history.push(url + '/summary')
          break
        case EventKey.FLOW_SUMMARY_FINISH:
          history.push(url + '/credit-card')
          break
      }
    }

    let id2 = EventEmitterService.subscribe(EventKey.FLOW_GO_BACK, () => {
      switch (location.pathname) {
        case url + '/pilot':
          history.replace(url + '/profile')
          break
        case url + '/customize':
          history.replace(url + '/pilot')
          break
        case url + '/summary':
          history.replace(url + '/customize')
          break
        default:
          history.goBack()
          break
      }
    })

    let id1 = EventEmitterService.subscribe(
      EventKey.FLOW_CUSTOMIZE_FINISH,
      () => {
        handleFlow(EventKey.FLOW_CUSTOMIZE_FINISH)
      }
    )

    let id3 = EventEmitterService.subscribe(
      EventKey.FLOW_PROFILE_FINISH,
      () => {
        handleFlow(EventKey.FLOW_PROFILE_FINISH)
      }
    )

    let id4 = EventEmitterService.subscribe(EventKey.FLOW_CC_FINISH, () => {
      handleFlow(EventKey.FLOW_CC_FINISH)
    })

    let id5 = EventEmitterService.subscribe(
      EventKey.FLOW_INSURANCE_PURCHASED_SUCCESS,
      (resp) => {
        history.push(url + '/congratulations', { resp: resp })
      }
    )

    let id6 = EventEmitterService.subscribe(
      EventKey.FLOW_SUMMARY_FINISH,
      (resp) => {
        handleFlow(EventKey.FLOW_SUMMARY_FINISH)
      }
    )

    let id7 = EventEmitterService.subscribe(
      EventKey.FLOW_CUSTOMIZE_VEHICLES_FINISH,
      () => {
        handleFlow(EventKey.FLOW_CUSTOMIZE_VEHICLES_FINISH)
      }
    )

    let id8 = EventEmitterService.subscribe(
      EventKey.FLOW_CUSTOMIZE_OPERATORS_FINISH,
      () => {
        handleFlow(EventKey.FLOW_CUSTOMIZE_OPERATORS_FINISH)
      }
    )

    let id9 = EventEmitterService.subscribe(
      EventKey.FLOW_COVERAGE_PLAN_PICKED,
      () => {
        handleFlow(EventKey.FLOW_COVERAGE_PLAN_PICKED)
      }
    )

    let id10 = EventEmitterService.subscribe(
      EventKey.FLOW_ISSUANCE_FINISHED,
      () => {
        handleFlow(EventKey.FLOW_ISSUANCE_FINISHED)
      }
    )
    let id11 = EventEmitterService.subscribe(
      EventKey.FLOW_PILOT_FINIISH,
      () => {
        handleFlow(EventKey.FLOW_PILOT_FINIISH)
      }
    )

    if (
      isInitialMount.current &&
      ViolationsService.isUserProfileOK(sessionContext.user) &&
      isInFlow(location.pathname)
    ) {
      isInitialMount.current = false
      handleFlow(EventKey.FLOW_PROFILE_FINISH)
    }

    return function cleanup() {
      EventEmitterService.unsubscribe(EventKey.FLOW_CUSTOMIZE_FINISH, id1)
      EventEmitterService.unsubscribe(EventKey.FLOW_GO_BACK, id2)
      EventEmitterService.unsubscribe(EventKey.FLOW_PROFILE_FINISH, id3)
      EventEmitterService.unsubscribe(EventKey.FLOW_CC_FINISH, id4)
      EventEmitterService.unsubscribe(
        EventKey.FLOW_INSURANCE_PURCHASED_SUCCESS,
        id5
      )
      EventEmitterService.unsubscribe(EventKey.FLOW_SUMMARY_FINISH, id6)
      EventEmitterService.unsubscribe(
        EventKey.FLOW_CUSTOMIZE_VEHICLES_FINISH,
        id7
      )
      EventEmitterService.unsubscribe(
        EventKey.FLOW_CUSTOMIZE_OPERATORS_FINISH,
        id8
      )
      EventEmitterService.unsubscribe(EventKey.FLOW_COVERAGE_PLAN_PICKED, id9)
      EventEmitterService.unsubscribe(EventKey.FLOW_ISSUANCE_FINISHED, id10)
      EventEmitterService.unsubscribe(EventKey.FLOW_PILOT_FINIISH, id10)
    }
  }, [sessionContext.user, location])

  const flowGoBack = () => EventEmitterService.dispatch(EventKey.FLOW_GO_BACK)

  return (
    <Switch>
      <Container>
        {location.pathname != '/insurance/congratulations' && (
          <>
            <BrowserView>
              <FlexView
                vAlignContent={'center'}
                className="space-between margin-top-basic">
                <FlexView vAlignContent={'bottom'}>
                  <div className="text-promotion black bold">
                    SkyWatch Renters Aircraft Insurance
                  </div>
                  {SessionService.isBroker() && (
                    <div className="margin-top-basic text-tiny margin-left-6">
                      <BlueGrayBox>{sessionContext.user.email}</BlueGrayBox>
                    </div>
                  )}
                </FlexView>
              </FlexView>
            </BrowserView>
            <MobileView>
              <FlexView
                className="margin-top-small space-between"
                vAlignContent={'center'}>
                <FlexView vAlignContent={'center'}>
                  {location.pathname != '/thankyou' && (
                    <KeyboardBackspaceIcon
                      onClick={flowGoBack}
                      className="black"
                    />
                  )}
                  <FlexView column vAlignContent={'center'}>
                    <div className="text-title bold margin-left-basic">
                      SkyWatch Renters Aircraft Insurance
                    </div>
                  </FlexView>
                  {SessionService.isBroker() && (
                    <div className="text-tiny margin-left-6">
                      <BlueGrayBox>{sessionContext.user.email}</BlueGrayBox>
                    </div>
                  )}
                </FlexView>
              </FlexView>
              <Divider />
            </MobileView>
            <Suspense fallback={<div />}>
              {!isMobile &&
                location.pathname != '/insurance/summary' &&
                location.pathname != '/insurance/credit-card' && (
                  <SkywatchProgressBar />
                )}
            </Suspense>

            <Grid container>
              <Grid item xs>
                {/* Incase path == /insurance , redirects to annual or monthly */}
                <Route exact path={url}>
                  <Redirect exact to={`${url}/profile`} />
                </Route>

                <LoginProtectedRoute
                  redirectTo="/insurance/profile"
                  path={`${path}/(monthly|annual)`}>
                  <Suspense fallback={<div />}></Suspense>
                </LoginProtectedRoute>

                <Route path={`${path}/email`}>
                  <Suspense fallback={<div />}>
                    <PasswordlessEmailForm />
                  </Suspense>
                </Route>

                <LoginProtectedRoute
                  redirectTo="/insurance/profile"
                  path={`${path}/customize`}>
                  <CustomizeCoverages />
                </LoginProtectedRoute>

                <Route redirectTo="/insurance/profile" path={`${path}/profile`}>
                  <UserProfileContext>
                    <Suspense fallback={<div />}>
                      <UserProfile />
                    </Suspense>
                  </UserProfileContext>
                </Route>

                <LoginProtectedRoute
                  redirectTo="/insurance/profile"
                  path={`${path}/pilot`}>
                  <PilotDetails />
                </LoginProtectedRoute>

                <Route path={`${path}/credit-card`}>
                  <Suspense fallback={<div />}>
                    <CheckoutForm />
                  </Suspense>
                </Route>

                <LoginProtectedRoute path={`${path}/summary`}>
                  <Suspense fallback={<div />}>
                    <InsuranceSetupSummary />
                    {/* <PlanSummary /> */}
                  </Suspense>
                </LoginProtectedRoute>

                <Suspense fallback={<div />}>
                  <FlowLogin finishedEvent={EventKey.FLOW_CUSTOMIZE_FINISH} />
                </Suspense>
              </Grid>
              <Grid item>
                {!isMobile &&
                  (location.pathname == '/insurance/customize' ||
                    location.pathname == '/insurance/credit-card') && (
                    <Suspense fallback={<div />}>
                      <PolicySummary />
                    </Suspense>
                  )}
              </Grid>
            </Grid>
          </>
        )}
        <Route path={`${path}/congratulations`}>
          <Suspense fallback={<div />}>
            <Congratulations />
          </Suspense>
        </Route>
      </Container>
    </Switch>
  )
}
